/**
 * Role Play Collections Screen (Phase 4 catalog rework)
 *
 * Three-section layout:
 *   1. Search bar — debounced; results render as a flat template list when active.
 *   2. "Your Role Plays" — horizontal strip of filled slots (compact card)
 *      plus a "Create custom" pill.
 *   3. Collection grid — 2-column grid of collection cards with completion
 *      badge ("3/8 done"). Tapping a collection navigates to RolePlayCollection.
 *
 * Replaces the old flat RolePlayListScreen. The `RolePlayList` route
 * keeps pointing here for backwards compatibility — the screen works
 * with either route name.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../components/ui/Ionicons';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useRolePlayStore,
  selectRolePlaySlots,
  selectRolePlayCap,
  selectRolePlayLoading,
  selectRolePlayErrorInfo,
  selectLoadSlots,
  selectClearRolePlayError,
  selectRolePlayCollections,
  selectRolePlayCollectionsLoading,
  selectRolePlayCollectionsErrorInfo,
  selectLoadCollections,
  selectClearCollectionsError,
  selectRolePlaySearchResults,
  selectRolePlaySearchLoading,
  selectRolePlaySearchQuery,
  selectSearchTemplates,
  selectClearSearch,
  type RolePlaySlot,
  type RolePlayCollection,
  type RolePlayTemplate,
} from '../store/rolePlayStore';
import { useConversationStore } from '../store/conversationStore';
import { selectStartNewSession } from '../store/conversationSelectors';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectActiveCourseId, selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../store/userPreferencesSelectors';
import { resolveBilingualContent, type BilingualContent } from '../types/lesson';
import { FilterChip } from '../components/roleplay/FilterChip';

const SEARCH_DEBOUNCE_MS = 300;
const SLOT_CARD_WIDTH = 200;

function useResolveBilingual() {
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  return useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, lessonLanguage, sourceLocale, targetLocale),
    [lessonLanguage, sourceLocale, targetLocale],
  );
}

// ── Slot card (horizontal strip) ──────────────────────────────────

interface SlotCardProps {
  slot: RolePlaySlot;
  onPress: (id: string) => void;
}

const SlotCard = React.memo(function SlotCard({ slot, onPress }: SlotCardProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const resolve = useResolveBilingual();
  const handlePress = useCallback(() => onPress(slot.id), [onPress, slot.id]);

  const title = resolve(slot.title);
  const scenario = resolve(slot.scenario);

  return (
    <TouchableOpacity
      testID={`roleplay-collections-slot-${slot.id}`}
      accessibilityRole="button"
      accessibilityLabel={t('roleplay.list.slotLabel', { title, scenario })}
      onPress={handlePress}
      activeOpacity={0.75}
      style={{ width: SLOT_CARD_WIDTH }}
    >
      <View style={[styles.slotCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: 14 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
        >
          {title}
        </Text>
        <Text
          allowFontScaling={false}
          numberOfLines={2}
          style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginTop: 4, ...ffs }}
        >
          {scenario}
        </Text>
      </View>
    </TouchableOpacity>
  );
});

// ── Collection card (grid) ────────────────────────────────────────

interface CollectionCardProps {
  collection: RolePlayCollection;
  onPress: (slug: string) => void;
}

/** Translate well-known collection slugs to user-facing titles when the
 *  bilingual content somehow comes back empty. Keeps the grid usable
 *  even if locale resolution returns a blank string. */
function fallbackCollectionTitle(slug: string): string {
  return slug.replace(/-/g, ' ');
}

const CollectionCard = React.memo(function CollectionCard({ collection, onPress }: CollectionCardProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const resolve = useResolveBilingual();
  const handlePress = useCallback(() => onPress(collection.slug), [onPress, collection.slug]);

  const title = resolve(collection.title) || fallbackCollectionTitle(collection.slug);
  const description = resolve(collection.description);
  const completed = collection.completedCount;
  const total = collection.templateCount;
  const progressLabel = t('roleplay.collections.progress', { completed, total });

  return (
    <TouchableOpacity
      testID={`roleplay-collections-card-${collection.slug}`}
      accessibilityRole="button"
      accessibilityLabel={t('roleplay.collections.cardLabel', { title, completed, total })}
      onPress={handlePress}
      activeOpacity={0.75}
      style={styles.collectionCardWrap}
    >
      <View style={[styles.collectionCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: 16 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
        >
          {title}
        </Text>
        <Text
          allowFontScaling={false}
          numberOfLines={2}
          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 4, flex: 1, ...ffs }}
        >
          {description}
        </Text>
        <View style={styles.progressRow}>
          <View style={[styles.progressTrack, { backgroundColor: theme.border }]}>
            <View
              style={[
                styles.progressFill,
                {
                  backgroundColor: theme.primary,
                  width: total > 0 ? `${Math.min(100, (completed / total) * 100)}%` : 0,
                },
              ]}
            />
          </View>
          <Text
            allowFontScaling={false}
            style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginLeft: 8, ...ffs }}
          >
            {progressLabel}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

// ── Search result row ─────────────────────────────────────────────

interface SearchResultRowProps {
  template: RolePlayTemplate;
  onPress: (id: string) => void;
}

const SearchResultRow = React.memo(function SearchResultRow({ template, onPress }: SearchResultRowProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const resolve = useResolveBilingual();
  const handlePress = useCallback(() => onPress(template.id), [onPress, template.id]);

  const title = resolve(template.title);
  const scenario = resolve(template.scenario);

  return (
    <TouchableOpacity
      testID={`roleplay-collections-search-result-${template.id}`}
      accessibilityRole="button"
      accessibilityLabel={t('roleplay.list.templateLabel', { title })}
      onPress={handlePress}
      activeOpacity={0.75}
    >
      <View style={[styles.searchResultCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: 15 * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {title}
        </Text>
        <Text
          allowFontScaling={false}
          numberOfLines={2}
          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 4, ...ffs }}
        >
          {scenario}
        </Text>
      </View>
    </TouchableOpacity>
  );
});

// ── Screen ────────────────────────────────────────────────────────

export function RolePlayCollectionsScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();

  // Slots
  const slots = useRolePlayStore(selectRolePlaySlots);
  const cap = useRolePlayStore(selectRolePlayCap);
  const slotsLoading = useRolePlayStore(selectRolePlayLoading);
  const slotsError = useRolePlayStore(selectRolePlayErrorInfo);
  const loadSlots = useRolePlayStore(selectLoadSlots);
  const clearSlotsError = useRolePlayStore(selectClearRolePlayError);

  // Collections
  const collections = useRolePlayStore(selectRolePlayCollections);
  const collectionsLoading = useRolePlayStore(selectRolePlayCollectionsLoading);
  const collectionsError = useRolePlayStore(selectRolePlayCollectionsErrorInfo);
  const loadCollections = useRolePlayStore(selectLoadCollections);
  const clearCollectionsError = useRolePlayStore(selectClearCollectionsError);

  // Search
  const searchResults = useRolePlayStore(selectRolePlaySearchResults);
  const searchLoading = useRolePlayStore(selectRolePlaySearchLoading);
  const searchQuery = useRolePlayStore(selectRolePlaySearchQuery);
  const searchTemplates = useRolePlayStore(selectSearchTemplates);
  const clearSearch = useRolePlayStore(selectClearSearch);

  const startNewSession = useConversationStore(selectStartNewSession);
  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);

  // Local input state — store keeps the *committed* query (used for the
  // API call); local state holds the unresolved keystrokes for the input.
  const [inputValue, setInputValue] = useState(searchQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filter state. Lives locally — we don't persist filters across
  // sessions. Difficulty filter is multi-select; completed is tri-state
  // (undefined = no filter, true = only completed, false = only un-completed).
  const [difficulties, setDifficulties] = useState<Set<number>>(new Set());
  const [completedFilter, setCompletedFilter] = useState<'true' | 'false' | undefined>(undefined);

  const toggleDifficulty = useCallback((level: number) => {
    setDifficulties((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const toggleCompleted = useCallback((value: 'true' | 'false') => {
    setCompletedFilter((prev) => (prev === value ? undefined : value));
  }, []);

  // Refresh slots on focus; load collections once per course.
  useFocusEffect(
    useCallback(() => {
      void loadSlots();
    }, [loadSlots]),
  );
  useFocusEffect(
    useCallback(() => {
      if (activeCourseId) void loadCollections(activeCourseId);
    }, [activeCourseId, loadCollections]),
  );
  // Reset search + filters when leaving the catalog. This screen stays mounted
  // (it's the roleplay-tab root), so without this a query/filter left active
  // lingers when the user returns — the grid + "Create custom" pill stay
  // hidden behind stale search mode.
  useFocusEffect(
    useCallback(
      () => () => {
        setInputValue('');
        setDifficulties(new Set());
        setCompletedFilter(undefined);
        clearSearch();
      },
      [clearSearch],
    ),
  );

  // Debounced search: any typed change schedules a search; filter
  // toggles trigger an immediate refetch (no typing latency to mask).
  // Empty input + no filters → clear; any filter without input also
  // hits the search endpoint so the result list reflects the filter.
  // Memoized so it's a STABLE reference across renders — otherwise it's a new
  // array every render, and listing it in the effect deps below makes the
  // effect run every render → `clearSearch()` → state update → re-render →
  // "Maximum update depth exceeded" (infinite loop). Recomputes only when the
  // `difficulties` Set actually changes.
  const difficultyArray = useMemo(() => Array.from(difficulties).sort(), [difficulties]);
  const difficultyKey = difficultyArray.join(',');
  useEffect(() => {
    if (!activeCourseId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = inputValue.trim();
    const hasFilters = difficultyArray.length > 0 || completedFilter !== undefined;
    if (trimmed.length === 0 && !hasFilters) {
      clearSearch();
      return;
    }
    const fire = () =>
      searchTemplates(activeCourseId, {
        q: trimmed.length > 0 ? trimmed : undefined,
        difficulty: difficultyArray.length > 0 ? difficultyArray : undefined,
        completed: completedFilter,
      });
    // Typing → debounce. Filter-only changes → immediate.
    if (trimmed.length > 0) {
      debounceRef.current = setTimeout(fire, SEARCH_DEBOUNCE_MS);
    } else {
      void fire();
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, activeCourseId, searchTemplates, clearSearch, difficultyKey, completedFilter, difficultyArray]);

  const handleOpenSlot = useCallback(
    (slotId: string) => navigation.navigate('RolePlayDetail', { slotId }),
    [navigation],
  );

  const handleCreateCustom = useCallback(async () => {
    await startNewSession('roleplay_generator');
    const sessionId = useConversationStore.getState().currentSessionId;
    if (sessionId) navigation.navigate('Chat', { sessionId });
  }, [startNewSession, navigation]);

  const handleOpenCollection = useCallback(
    (slug: string) => navigation.navigate('RolePlayCollection', { slug }),
    [navigation],
  );

  const handleOpenTemplate = useCallback(
    (templateId: string) => navigation.navigate('RolePlayTemplatePreview', { templateId }),
    [navigation],
  );

  const handleSlotsRetry = useCallback(() => {
    clearSlotsError();
    void loadSlots();
  }, [clearSlotsError, loadSlots]);

  const handleCollectionsRetry = useCallback(() => {
    clearCollectionsError();
    if (activeCourseId) void loadCollections(activeCourseId);
  }, [clearCollectionsError, activeCourseId, loadCollections]);

  const isFull = slots.length >= cap;
  const hasFilters = difficultyArray.length > 0 || completedFilter !== undefined;
  const isSearching = searchQuery.trim().length > 0 || hasFilters;

  const collectionRenderItem = useCallback(
    ({ item }: { item: RolePlayCollection }) => (
      <CollectionCard collection={item} onPress={handleOpenCollection} />
    ),
    [handleOpenCollection],
  );
  const collectionKey = useCallback((c: RolePlayCollection) => c.slug, []);

  const searchRenderItem = useCallback(
    ({ item }: { item: RolePlayTemplate }) => (
      <SearchResultRow template={item} onPress={handleOpenTemplate} />
    ),
    [handleOpenTemplate],
  );
  const searchKey = useCallback((tpl: RolePlayTemplate) => tpl.id, []);

  // Memoize the slots horizontal data — the create-custom pill is a
  // separate render below so we only need slot rows in the list.
  const slotKey = useCallback((s: RolePlaySlot) => s.id, []);
  const slotRenderItem = useCallback(
    ({ item }: { item: RolePlaySlot }) => <SlotCard slot={item} onPress={handleOpenSlot} />,
    [handleOpenSlot],
  );
  const slotsHasContent = slots.length > 0 || slotsLoading || !!slotsError;

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('roleplay.list.title')} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <ScrollView
          testID="roleplay-collections-screen"
          contentContainerStyle={styles.scrollContent}
        >
          {/* ── Search bar ── */}
          <View style={[styles.searchBar, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <Ionicons name="search" size={18} color={theme.textSecondary} />
            <TextInput
              testID="roleplay-collections-search-input"
              accessibilityLabel={t('roleplay.collections.searchPlaceholder')}
              placeholder={t('roleplay.collections.searchPlaceholder')}
              placeholderTextColor={theme.textSecondary}
              value={inputValue}
              onChangeText={setInputValue}
              autoCorrect={false}
              autoCapitalize="none"
              returnKeyType="search"
              style={{ flex: 1, fontSize: 15 * fontScale, color: theme.text, marginLeft: 8, ...ffs }}
            />
            {inputValue.length > 0 && (
              <TouchableOpacity
                testID="roleplay-collections-search-clear"
                accessibilityRole="button"
                accessibilityLabel={t('roleplay.collections.searchClear')}
                onPress={() => setInputValue('')}
              >
                <Ionicons name="close-circle" size={18} color={theme.textSecondary} />
              </TouchableOpacity>
            )}
          </View>

          {/* Filter chips — visible when searching or when any filter is
              set. Difficulty is multi-select; completed is tri-state. */}
          <View testID="roleplay-collections-filters" style={styles.filterRow}>
            {[1, 2, 3, 4, 5].map((level) => (
              <FilterChip
                key={`difficulty-${level}`}
                label={t('roleplay.collections.difficultyLabel', { level })}
                active={difficulties.has(level)}
                onPress={() => toggleDifficulty(level)}
                testID={`roleplay-collections-filter-difficulty-${level}`}
                accessibilityLabel={t('roleplay.collections.filterDifficultyLabel', { level })}
              />
            ))}
            <FilterChip
              label={t('roleplay.collections.filterCompleted')}
              active={completedFilter === 'true'}
              onPress={() => toggleCompleted('true')}
              testID="roleplay-collections-filter-completed"
              accessibilityLabel={t('roleplay.collections.filterCompleted')}
            />
            <FilterChip
              label={t('roleplay.collections.filterUncompleted')}
              active={completedFilter === 'false'}
              onPress={() => toggleCompleted('false')}
              testID="roleplay-collections-filter-uncompleted"
              accessibilityLabel={t('roleplay.collections.filterUncompleted')}
            />
          </View>

          {/* When searching, show flat results — collapse the rest of the page. */}
          {isSearching ? (
            <View testID="roleplay-collections-search-results">
              {searchLoading && searchResults.length === 0 && (
                <View style={{ paddingVertical: 16 }}>
                  <ActivityIndicator size="small" color={theme.primary} />
                </View>
              )}
              {!searchLoading && searchResults.length === 0 && (
                <View style={styles.emptyHint}>
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: 14 * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
                  >
                    {t('roleplay.collections.searchEmpty')}
                  </Text>
                </View>
              )}
              <FlatList
                data={searchResults}
                renderItem={searchRenderItem}
                keyExtractor={searchKey}
                contentContainerStyle={{ gap: 8 }}
                scrollEnabled={false}
              />
            </View>
          ) : (
            <>
              {/* ── "Your Role Plays" strip ── */}
              {slotsHasContent && (
                <View testID="roleplay-collections-section-yours" style={styles.sectionHeader}>
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: 16 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
                  >
                    {t('roleplay.list.sectionYours')}
                  </Text>
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: 14 * fontScale, color: theme.textSecondary, marginLeft: 8, ...ffs }}
                  >
                    {slots.length}/{cap}
                  </Text>
                </View>
              )}

              {slotsError && (
                <View
                  testID="roleplay-collections-slots-error"
                  style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}
                  >
                    {errorMessage(slotsError)}
                  </Text>
                  <TouchableOpacity
                    testID="roleplay-collections-slots-error-retry"
                    accessibilityRole="button"
                    accessibilityLabel={t('roleplay.list.loadErrorRetry')}
                    onPress={handleSlotsRetry}
                    style={[styles.retryButton, { borderColor: theme.border }]}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                    >
                      {t('roleplay.list.loadErrorRetry')}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              {slotsLoading && slots.length === 0 && !slotsError && (
                <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 12 }} />
              )}

              {slots.length > 0 && (
                <FlatList
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  data={slots}
                  renderItem={slotRenderItem}
                  keyExtractor={slotKey}
                  contentContainerStyle={{ gap: 10, paddingVertical: 4 }}
                />
              )}

              {/* Create-custom pill (always visible, disabled when full). The
                  testID + accessibilityLabel reflect the full state so the
                  block is both screen-reader-legible and assertable in E2E. */}
              <TouchableOpacity
                testID={
                  isFull
                    ? 'roleplay-collections-create-custom-full'
                    : 'roleplay-collections-create-custom'
                }
                accessibilityRole="button"
                accessibilityLabel={
                  isFull
                    ? t('roleplay.list.slotsFull', { cap })
                    : t('roleplay.list.createCustom')
                }
                accessibilityState={{ disabled: isFull || slotsLoading }}
                onPress={handleCreateCustom}
                disabled={isFull || slotsLoading}
                activeOpacity={0.75}
                style={[
                  styles.createCustomCard,
                  { borderColor: theme.border, opacity: isFull || slotsLoading ? 0.5 : 1 },
                ]}
              >
                <Ionicons
                  name="add"
                  size={22}
                  color={isFull ? theme.textSecondary : theme.primary}
                />
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: 14 * fontScale,
                    color: isFull ? theme.textSecondary : theme.primary,
                    fontWeight: '600',
                    marginLeft: 8,
                    ...ffs,
                  }}
                >
                  {isFull ? t('roleplay.list.slotsFull', { cap }) : t('roleplay.list.createCustom')}
                </Text>
              </TouchableOpacity>

              {/* ── Collections grid ── */}
              <View style={[styles.sectionHeader, { marginTop: 24 }]}>
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 16 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
                >
                  {t('roleplay.collections.sectionTitle')}
                </Text>
              </View>

              {collectionsError && (
                <View
                  testID="roleplay-collections-error"
                  style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}
                  >
                    {errorMessage(collectionsError)}
                  </Text>
                  <TouchableOpacity
                    testID="roleplay-collections-error-retry"
                    accessibilityRole="button"
                    accessibilityLabel={t('roleplay.list.loadErrorRetry')}
                    onPress={handleCollectionsRetry}
                    style={[styles.retryButton, { borderColor: theme.border }]}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                    >
                      {t('roleplay.list.loadErrorRetry')}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              {collectionsLoading && collections.length === 0 && !collectionsError && (
                <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 24 }} />
              )}

              {!collectionsLoading && collections.length === 0 && !collectionsError && (
                <View testID="roleplay-collections-empty" style={styles.emptyHint}>
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: 14 * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
                  >
                    {t('roleplay.collections.emptyHint')}
                  </Text>
                </View>
              )}

              {collections.length > 0 && (
                <FlatList
                  data={collections}
                  renderItem={collectionRenderItem}
                  keyExtractor={collectionKey}
                  numColumns={2}
                  columnWrapperStyle={{ gap: 12 }}
                  contentContainerStyle={{ gap: 12 }}
                  scrollEnabled={false}
                />
              )}
            </>
          )}
        </ScrollView>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    padding: 16,
    gap: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  emptyHint: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  slotCard: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    minHeight: 80,
  },
  createCustomCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 10,
    borderWidth: 2,
    borderStyle: 'dashed',
    minHeight: 48,
  },
  collectionCardWrap: {
    flex: 1,
  },
  collectionCard: {
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    minHeight: 130,
    flex: 1,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  progressTrack: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  searchResultCard: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  retryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
});
